import { isDev, isEmpty } from '../helpers/common';
import throttle from 'lodash/throttle';
import { captureException } from './logService';
import { RawCurrentUser } from '../mobx/interfaces/user';

interface Window {
  rudderanalytics?: {
    identify: (userId: string, traits?: Record<string, unknown>) => void;
    track: (eventName: string, properties?: Record<string, unknown>) => void;
  };
  amplitude?: {
    setGroup: (groupType: string, groupName: string) => void;
  };
  Intercom?: {
    (
      command: 'update',
      data: { company: { company_id: string; name: string; created_at?: string | number | Date } }
    ): void;
  };
}

type AnalyticEventParams = Record<string, unknown> | undefined;

declare const window: Window;

interface CompanyAnalyticsData {
  id: string;
  name: string;
  isOwner?: boolean;
  isAdmin?: boolean;
  customerRole?: number | null;
  created_on?: string | number | Date;
}

interface PageViewParams {
  visit_url: string;
}

/**
 * @desc set use and company for analytics
 */

export const setAnalyticUserAndCompany = (user: RawCurrentUser, company: CompanyAnalyticsData) => {
  try {
    if (isDev()) {
      return;
    }
    if (window.rudderanalytics) {
      window.rudderanalytics.identify(user.id, {
        email: user.email,
        name: user.name,
        createdAt: user?.user_created_on ? new Date(user?.user_created_on * 1000).toISOString() : undefined,
        isOwner: company?.isOwner ? true : undefined,
        isAdmin: company?.isAdmin ? true : undefined,
        isCustomer: company && !isEmpty(company?.customerRole) ? true : undefined
      });

      if (window.amplitude) {
        window.amplitude.setGroup('workspaceId', company.id);
      }

      if (window.Intercom) {
        window.Intercom('update', {
          company: {
            company_id: company.id,
            name: company.name,
            created_at: company?.created_on
          }
        });
      }
    }
  } catch (e) {
    captureException(e);
  }
};

/**
 * @desc clear user information for analytics
 */
export const clearAnalyticsUser = () => {
  try {
    if (isDev()) {
      return;
    }
    // Need to implement logic for clear analytics
  } catch (e) {
    captureException(e);
  }
};

/**
 * @desc track activity in analytics
 */
export const trackAnalyticActivity = (name: string, params?: AnalyticEventParams) => {
  try {
    if (isDev()) {
      return;
    }
    if (window.rudderanalytics) {
      window.rudderanalytics.track(name, params);
    }
  } catch (e) {
    captureException(e);
  }
};

/**
 * @desc track page view
 */
export const trackPage = (params: PageViewParams) => {
  try {
    const fnTrackPage = throttle(() => {
      if (isDev()) {
        return;
      }
      // Nothing implemented! need to implement here
    }, 500);
    fnTrackPage();
  } catch (e) {
    captureException(e);
  }
};

/**
 * @desc track page view
 */
export const workspaceSetAnalytics = (workspace_id: string) => {
  try {
    if (isDev()) {
      return;
    }
    // Nothing implemented! need to implement here
  } catch (e) {
    captureException(e);
  }
};
